/**
 * The one place that decides which bindings are live right now.
 *
 * Pure on purpose. Scope precedence and conflict resolution are the two rules
 * most likely to be got subtly wrong and the two least pleasant to debug
 * through a real keyboard, so they live in a function that takes plain data and
 * returns plain data. `CommandsProvider` is then a thin shell that feeds it
 * React state and hands the result to `useHotkeys`/`useHotkeySequences`.
 */
import { detectPlatform } from '@tanstack/react-hotkeys';
import { COMMANDS, scopeOf } from './catalog';
import { bindingKey } from './keymap';
import {
  isSequenceBinding,
  type Binding,
  type CommandId,
  type CommandKeyOptions,
  type CommandScope,
  type Keymap,
  type Platform,
} from './types';

/** Why a binding is or is not live. Surfaced in the shortcuts panel. */
export type BindingStatus =
  /** Live: it will fire. */
  | 'active'
  /** Its scope is not on the active stack — the surface that owns it is unmounted. */
  | 'scope-inactive'
  /** In scope, but nothing has registered behaviour for it. */
  | 'no-handler'
  /** A handler exists but reported itself unavailable (nothing to save, etc.). */
  | 'handler-disabled'
  /** A more specific scope claims the same chord and wins. */
  | 'shadowed';

/** One (command, binding) pair, with the verdict on whether it fires. */
export interface ResolvedBinding {
  readonly commandId: CommandId;
  readonly binding: Binding;
  readonly scope: CommandScope;
  /** Platform-normalized chord identity — see `bindingKey`. */
  readonly key: string;
  readonly status: BindingStatus;
  /** `status === 'active'`. What the binder passes as `enabled`. */
  readonly enabled: boolean;
  /** Set only when `status === 'shadowed'`: the command that took the chord. */
  readonly shadowedBy?: CommandId;
  /** Hotkey options for this row, with this layer's defaults applied. */
  readonly options: CommandKeyOptions;
}

export interface ResolveBindingsInput {
  readonly keymap: Keymap;
  /**
   * Active scopes, OUTERMOST FIRST. `'global'` is expected at index 0 and the
   * innermost mounted surface last, because a later index means higher
   * precedence — that is the whole mechanism by which a viewer binding beats a
   * global one on the same chord.
   */
  readonly activeScopes: readonly CommandScope[];
  /**
   * Command ids with a mounted handler, mapped to whether that handler is
   * currently available.
   *
   * One map rather than a `handlers` set plus a `disabled` set: those were two
   * views of the same fact, and the provider was rebuilding both from one map
   * on every pass.
   */
  readonly handlers: ReadonlyMap<CommandId, boolean>;
  readonly platform?: Platform;
}

/**
 * Resolve every binding in the keymap against the active scopes.
 *
 * Every binding is returned, including the dead ones, and the caller registers
 * them all — a row that cannot fire is registered with `enabled: false` rather
 * than skipped. Two reasons: TanStack updates a soft-disabled registration in
 * place via `setOptions` instead of unregistering and re-registering (so
 * mounting a viewer flips booleans rather than churning the manager's store),
 * and the dead rows stay visible in TanStack Hotkeys devtools, which is where
 * "why didn't my shortcut fire" gets answered.
 *
 * Conflict resolution, in order:
 *   1. only eligible rows compete — in an active scope, with a live handler;
 *   2. the deepest active scope wins, so a leaf shadows `'global'`;
 *   3. a tie inside one scope goes to catalog order. That case is a genuine
 *      double-binding, which `findKeymapConflicts` reports separately so it
 *      can be fixed rather than silently arbitrated here.
 */
export function resolveBindings(
  input: ResolveBindingsInput,
): readonly ResolvedBinding[] {
  const { keymap, activeScopes, handlers } = input;
  // Once per pass, not once per binding. `detectPlatform()` reads
  // `navigator.platform` AND `navigator.userAgent` and lowercases both, so
  // leaving it to `bindingKey`'s own fallback cost one call and two string
  // allocations per binding on every resolve — and a resolve happens on every
  // scope mount and every `enabled` flip.
  const platform = input.platform ?? detectPlatform();

  const precedence = new Map<CommandScope, number>();
  activeScopes.forEach((scope, index) => precedence.set(scope, index));

  interface Row {
    commandId: CommandId;
    binding: Binding;
    scope: CommandScope;
    key: string;
    options: CommandKeyOptions;
    status: BindingStatus;
  }

  const rows: Row[] = [];

  // A plain `for` rather than `forEach` with an index: the index existed only
  // to record catalog order for tie-breaks, and insertion order into `rows`
  // already IS catalog order — the tie-break reads it by keeping the incumbent.
  for (const command of COMMANDS) {
    const scope = scopeOf(command);

    // Ordered so the message names the outermost cause: an unmounted surface
    // explains a missing handler, not the other way round.
    const status: BindingStatus = !precedence.has(scope)
      ? 'scope-inactive'
      : !handlers.has(command.id)
        ? 'no-handler'
        : handlers.get(command.id) === false
          ? 'handler-disabled'
          : 'active';

    for (const binding of keymap[command.id] ?? []) {
      rows.push({
        commandId: command.id,
        binding,
        scope,
        key: bindingKey(binding, platform),
        options: bindingOptions(command.options, binding),
        status,
      });
    }
  }

  // Winner per chord, among live rows only.
  const winners = new Map<string, Row>();
  for (const row of rows) {
    if (row.status !== 'active') continue;
    const held = winners.get(row.key);
    if (!held) {
      winners.set(row.key, row);
      continue;
    }
    const challengerDepth = precedence.get(row.scope) ?? -1;
    const heldDepth = precedence.get(held.scope) ?? -1;
    if (challengerDepth > heldDepth) winners.set(row.key, row);
    // Equal depth keeps the incumbent, which is the earlier catalog entry.
  }

  // One spread rather than three near-identical eight-field literals: only
  // `status`, `enabled` and `shadowedBy` differ between the branches, and
  // copying the other five three times is how one of them drifts.
  return rows.map((row) => {
    if (row.status !== 'active') return { ...row, enabled: false };
    const winner = winners.get(row.key);
    if (winner === row) return { ...row, enabled: true };
    // Lost the chord. Usually to another command; but a command carrying two
    // aliases that normalize to one chord loses to its own other row, and then
    // `shadowedBy` naming itself is the honest answer.
    return {
      ...row,
      status: 'shadowed',
      enabled: false,
      shadowedBy: winner?.commandId ?? row.commandId,
    };
  });
}

/**
 * This layer's hotkey options for one binding.
 *
 * Deliberately thin. `ignoreInputs` is left undefined unless a command pins it,
 * because TanStack's own default is derived from the chord's shape and is
 * better than a blanket rule: a bare `R` is ignored while the caret is in a
 * text field, a `Mod+S` is not, and `Escape` always fires. Hard-coding
 * `ignoreInputs: true` here — the obvious-looking move — would break `Mod+S`
 * in the report editor, which is the only place it is ever pressed.
 *
 * `preventDefault` defaults to true (TanStack's default) and is worth pinning
 * to false per command for keys the browser or an IME needs — see
 * `general.closeOverlay`.
 *
 * `stopPropagation` is the one default this layer OVERRIDES, and it has to.
 * TanStack defaults it to true and the manager registers on `document`, so
 * every matched chord called `event.stopPropagation()` and starved every
 * `window`-level listener in the app of that key. Measured breakage: with a
 * design point selected, Escape stopped closing the write-up drawer
 * (`WriteupDrawer`'s own `window` listener) and stopped exiting assistant
 * fullscreen (`assistant-sidebar`) — it closed the invisible detail panel
 * underneath instead. The command system taking a key does not entitle it to
 * hide that key from everything else on the page; a command that genuinely
 * needs to can opt in.
 */
export function bindingOptions(
  declared: CommandKeyOptions | undefined,
  binding: Binding,
): CommandKeyOptions {
  const options: CommandKeyOptions = { stopPropagation: false, ...declared };
  if (!isSequenceBinding(binding) && options.sequenceTimeout !== undefined) {
    // A timeout on a chord is meaningless; drop it rather than pass it on.
    const { sequenceTimeout: _ignored, ...rest } = options;
    return rest;
  }
  return options;
}

/** Every resolved row for one command, in keymap order. */
export function bindingsForCommand(
  resolved: readonly ResolvedBinding[],
  id: CommandId,
): readonly ResolvedBinding[] {
  return resolved.filter((row) => row.commandId === id);
}
